import MoreVertIcon from '@mui/icons-material/MoreVert';
import PreviewIcon from '@mui/icons-material/Preview';
import SaveIcon from '@mui/icons-material/Save';
import {
  Box,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid-premium';
import { MouseEvent, useMemo, useState } from 'react';
import dayjs from 'dayjs';

import { ApplicationCycleDataGridRow } from '@/models/Admin/AdminApplicationCycle/AdminApplicationCycle';
import { ApplicationCycle } from '@/models/ApplicationCycle';
import useAdminApplicationCycle from '@/hooks/Admin/useAdminApplicationCycle';

const ACTION_BUTTON_SIZE = 28;

const toUtcMidnightDate = (value: string | number | Date): Date => {
  const dateOnly =
    typeof value === 'string' && /^(\d{4}-\d{2}-\d{2})/.test(value)
      ? value.substring(0, 10)
      : dayjs(value).format('YYYY-MM-DD');

  return new Date(`${dateOnly}T00:00:00.000Z`);
};

type ApplicationCycleActionsProps = {
  params: GridRenderCellParams<ApplicationCycleDataGridRow>;
  touchedRowIds: number[];
  handleSuccess: (currentRowId: ApplicationCycle) => void;
  handleError: (error: unknown) => void;
};

const ApplicationCycleActions = ({
  params,
  touchedRowIds,
  handleSuccess,
  handleError,
}: ApplicationCycleActionsProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const rowId = Number(params.id);

  const { updateApplicationCycle } = useAdminApplicationCycle();

  const actionsDisabled = useMemo(() => {
    return !touchedRowIds.includes(rowId) || loading;
  }, [rowId, touchedRowIds, loading]);
  const hasUnsavedChanges = useMemo(() => touchedRowIds.includes(rowId), [rowId, touchedRowIds]);

  const menuOpen = menuAnchorEl != null;

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handlePreview = () => {
    handleMenuClose();
    window.open(`/application-preview/${rowId}`, '_blank', 'noopener');
  };

  const handleSave = async () => {
    handleMenuClose();
    setLoading(true);

    const updatedAppCycleId = rowId;

    // Keep the selected calendar day stable regardless of local timezone.
    const updatedOpenDate = toUtcMidnightDate(params.row.openDate);
    const updatedCloseDate = toUtcMidnightDate(params.row.closeDate);

    try {
      const response = await updateApplicationCycle(
        updatedAppCycleId,
        updatedOpenDate,
        updatedCloseDate
      );
      handleSuccess(response);
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
      }}
    >
      {loading ? (
        <CircularProgress
          size={ACTION_BUTTON_SIZE}
          sx={{
            color: 'green',
          }}
        />
      ) : (
        <>
          <Tooltip title={hasUnsavedChanges ? 'Save changes' : 'No changes to save'}>
            <span>
              <IconButton
                aria-label='save application cycle changes'
                disabled={actionsDisabled}
                onClick={() => {
                  void handleSave();
                }}
                sx={{
                  maxWidth: ACTION_BUTTON_SIZE,
                  minWidth: ACTION_BUTTON_SIZE,
                  maxHeight: ACTION_BUTTON_SIZE,
                  minHeight: ACTION_BUTTON_SIZE,
                  color: hasUnsavedChanges ? 'warning.main' : 'action.disabled',
                }}
              >
                <SaveIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton
            aria-label='application cycle actions'
            aria-controls={menuOpen ? `app-cycle-actions-${params.id}` : undefined}
            aria-haspopup='true'
            aria-expanded={menuOpen ? 'true' : undefined}
            sx={{
              maxWidth: ACTION_BUTTON_SIZE,
              minWidth: ACTION_BUTTON_SIZE,
              maxHeight: ACTION_BUTTON_SIZE,
              minHeight: ACTION_BUTTON_SIZE,
            }}
            onClick={handleMenuOpen}
          >
            <MoreVertIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </>
      )}

      <Menu
        id={`app-cycle-actions-${rowId}`}
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handlePreview}>
          <ListItemIcon>
            <PreviewIcon fontSize='small' sx={{ color: 'secondary.main' }} />
          </ListItemIcon>
          <ListItemText>Preview</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleSave} disabled={actionsDisabled}>
          <ListItemIcon>
            <SaveIcon fontSize='small' sx={{ color: 'success.main' }} />
          </ListItemIcon>
          <ListItemText>Save</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ApplicationCycleActions;
